<template>
  <div class="delivery-note-create-page main-content">
    <form v-if="!initLoading" action="" @submit.prevent="submitDeliveryNoteData">
      <div class="page-header">
        <h3 v-if="$route.name === 'delivery-notes.edit'" class="page-title">{{ $t('deliveryNotes.edit_delivery_note') }}</h3>
        <h3 v-else class="page-title">{{ $t('deliveryNotes.new_delivery_note') }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item"><router-link slot="item-title" to="/supplier/dashboard">{{ $t('general.home') }}</router-link></li>
          <li class="breadcrumb-item"><router-link slot="item-title" to="/supplier/delivery-notes">{{ $tc('deliveryNotes.delivery_note', 2) }}</router-link></li>
          <li v-if="$route.name === 'delivery-notes.edit'" class="breadcrumb-item">{{ $t('deliveryNotes.edit_delivery_note') }}</li>
          <li v-else class="breadcrumb-item">{{ $t('deliveryNotes.new_delivery_note') }}</li>
        </ol>
        <div class="page-actions row">
          <a v-if="$route.name === 'delivery-notes.edit'" :href="`/deliveryNotes/pdf/${newDeliveryNote.unique_hash}`" target="_blank" class="mr-3 base-button btn btn-outline-primary default-size invoice-action-btn" outline color="theme">
            {{ $t('general.view_pdf') }}
          </a>
          <base-button
            :loading="isLoading"
            :disabled="isLoading"
            icon="save"
            class="invoice-action-btn"
            color="theme"
            type="submit">
            {{ $t('deliveryNotes.save_delivery_note') }}
          </base-button>
        </div>
      </div>
      <div class="row delivery-note-input-group">
        <div class="col-md-5 delivery-note-customer-container">
          <div
            v-if="selectedCustomer" class="show-customer">
            <div class="row px-2 mt-1">
              <div v-if="selectedCustomer.billing_address" class="col col-6">
                <div class="row address-menu">
                  <label class="col-sm-4 px-2 title">{{ $t('general.bill_to') }}</label>
                  <div class="col-sm p-0 px-2 content">
                    <label v-if="selectedCustomer.billing_address.name">
                      {{ selectedCustomer.billing_address.name }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.address_street_1">
                      {{ selectedCustomer.billing_address.address_street_1 }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.address_street_2">
                      {{ selectedCustomer.billing_address.address_street_2 }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.city && selectedCustomer.billing_address.state">
                      {{ selectedCustomer.billing_address.city }}, {{ selectedCustomer.billing_address.state }} {{ selectedCustomer.billing_address.zip }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.country">
                      {{ selectedCustomer.billing_address.country.name }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.phone">
                      {{ selectedCustomer.billing_address.phone }}
                    </label>
                  </div>
                </div>
              </div>
              <div v-if="selectedCustomer.shipping_address" class="col col-6">
                <div class="row address-menu">
                  <label class="col-sm-4 px-2 title">{{ $t('general.ship_to') }}</label>
                  <div class="col-sm p-0 px-2 content">
                    <label v-if="selectedCustomer.shipping_address.name">
                      {{ selectedCustomer.shipping_address.name }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.address_street_1">
                      {{ selectedCustomer.shipping_address.address_street_1 }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.address_street_2">
                      {{ selectedCustomer.shipping_address.address_street_2 }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.city && selectedCustomer.shipping_address">
                      {{ selectedCustomer.shipping_address.city }}, {{ selectedCustomer.shipping_address.state }} {{ selectedCustomer.shipping_address.zip }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.country" class="country">
                      {{ selectedCustomer.shipping_address.country.name }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.phone" class="phone">
                      {{ selectedCustomer.shipping_address.phone }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="customer-content mb-1">
              <label class="email">{{ selectedCustomer.name }}</label>
              <label class="action" @click="editCustomer">{{ $t('general.edit') }}</label>
              <label class="action" @click="removeCustomer">{{ $t('general.deselect') }}</label>
            </div>
          </div>

          <base-popup v-else :class="['add-customer', {'customer-required': $v.selectedCustomer.$error}]" >
            <div slot="activator" class="add-customer-action">
              <font-awesome-icon icon="user" class="customer-icon"/>
              <div>
                <label>{{ $t('customers.new_customer') }} <span class="text-danger"> * </span></label>
                <p v-if="$v.selectedCustomer.$error && !$v.selectedCustomer.required" class="text-danger"> {{ $t('pos.errors.required') }} </p>
              </div>
            </div>
            <customer-select-popup type="deliveryNote" />
          </base-popup>
        </div>
        <div class="col delivery-note-input">
          <div class="row mb-3">
            <div class="col collapse-input">
              <label>{{ $t('deliveryNotes.delivery_note_date') }}<span class="text-danger"> * </span></label>
              <base-date-picker
                v-model="newDeliveryNote.delivery_note_date"
                :calendar-button="true"
                calendar-button-icon="calendar"
                @change="$v.newDeliveryNote.delivery_note_date.$touch()"
              />
              <span v-if="$v.newDeliveryNote.delivery_note_date.$error && !$v.newDeliveryNote.delivery_note_date.required" class="text-danger"> {{ $t('validation.required') }} </span>
            </div>

          </div>
          <div class="row mt-4">
            <div class="col collapse-input">
              <label>{{ $t('deliveryNotes.delivery_note_number') }}</label>
              <base-prefix-input
                v-model="deliveryNoteNumAttribute"
                :invalid="$v.deliveryNoteNumAttribute.$error"
                :prefix="deliveryNotePrefix"
                icon="hashtag"
                :readonly="true"
                @input="$v.deliveryNoteNumAttribute.$touch()"
              />
              <span v-show="$v.deliveryNoteNumAttribute.$error && !$v.deliveryNoteNumAttribute.required" class="text-danger mt-1"> {{ $tc('pos.errors.required') }}  </span>
              <span v-show="!$v.deliveryNoteNumAttribute.numeric" class="text-danger mt-1"> {{ $tc('validation.numbers_only') }}  </span>
            </div>
            <div class="col collapse-input">
              <label>{{ $t('deliveryNotes.ref_number') }}</label>
              <base-input
                v-model="newDeliveryNote.reference_number"
                :invalid="$v.newDeliveryNote.reference_number.$error"
                icon="hashtag"
                @input="$v.newDeliveryNote.reference_number.$touch()"
              />
              <div v-if="$v.newDeliveryNote.reference_number.$error" class="text-danger">{{ $tc('validation.ref_number_maxlength') }}</div>
            </div>
          </div>
        </div>
      </div>

      <table class="item-table">
        <colgroup>
          <col style="width: 2%;">
           <!--
          <col style="width: 20%;">
          <col style="width: 20%;">
           -->
          <col style="width: 35%;">
          <col style="width: 35%;">
          <col style="width: 10%;">
          <!--
          <col style="width: 15%;">
          <col style="width: 15%;">
          -->
        </colgroup>
        <thead class="item-table-header">
          <tr>
            <th class="text-left">
              <span class="column-heading item-checkbox">
                <input type="checkbox"
                    v-if="POHasItems"
                    v-model="selectAllDeliveryNoteItem"
                    @change="selectAllDeliveryNoteItems"
                />
              </span>
            </th>
            <th class="text-left">
              <span class="column-heading">
                {{ $tc('items.item',2) }}
              </span>
            </th>
            <th class="text-left">
              <span class="column-heading">
                {{ $tc('items.part_number',2) }}
              </span>
            </th>
            <th class="text-left">
              <span class="column-heading">
                {{ $t('pos.item.quantity') }}
              </span>
            </th>
            <!-- <th class="text-left">
              <span class="column-heading">
                {{ $t('pos.item.price') }}
              </span>
            </th>
            <th class="text-right">
              <span class="column-heading amount-heading">
                {{ $t('pos.item.amount') }}
              </span>
            </th> -->
          </tr>
        </thead>
        <draggable v-model="newDeliveryNote.items" class="item-body" tag="tbody" handle=".handle">
          <item
            v-for="(item, index) in newDeliveryNote.items"
            :ItemId="ItemId"
            :key="item.id"
            :index="index"
            :item-data="item"
            :currency="currency"
            :order-items="newDeliveryNote.items"
            @remove="removeItem"
            @update="updateItem"
            @itemValidate="checkItemsData"
          />
        </draggable>
      </table>
      <div class="add-item-action" @click="addItem" >
        <font-awesome-icon icon="shopping-basket" class="mr-2"/>
        {{ $t('deliveryNotes.add_item') }}
      </div>
       <div class="add-item-action" @click="removeMultipleItems" v-if="selectedDeliveryNoteItems.length">
        <font-awesome-icon :icon="['fas', 'trash']" class="mr-2"/>
        {{ $t('general.delete') }}
      </div>
      <div class="delivery-note-foot">
        <div>
          <label>{{ $t('deliveryNotes.notes') }}</label>
          <base-text-area
            v-model="newDeliveryNote.notes"
            rows="3"
            cols="50"
            @input="$v.newDeliveryNote.notes.$touch()"
          />
          <div v-if="$v.newDeliveryNote.notes.$error">
            <span v-if="!$v.newDeliveryNote.notes.maxLength" class="text-danger">{{ $t('validation.notes_maxlength') }}</span>
          </div>
          <label class="mt-3 mb-1 d-block">{{ $t('deliveryNotes.delivery_note_template') }} <span class="text-danger"> * </span></label>
          <base-button type="button" class="btn-template" icon="pencil-alt" right-icon @click="openTemplateModal" >
            <span class="mr-4"> {{ $t('deliveryNotes.delivery_note_template') }} {{ getTemplateId }} </span>
          </base-button>
        </div>

        <!-- <div class="delivery-note-total">
          <div class="section">
            <label class="po-label">{{ $t('pos.sub_total') }}</label>
            <label class="po-amount">
              <div v-html="$utils.formatMoney(subtotal, currency)" />
            </label>
          </div>
          <div class="section border-top mt-3">
            <label class="delivery-note-label">{{ $t('pos.total') }} {{ $t('pos.amount') }}:</label>
            <label class="delivery-note-amount total">
              <div v-html="$utils.formatMoney(subtotal, currency)" />
            </label>
          </div>
        </div> -->


      </div>
    </form>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import MultiSelect from 'vue-multiselect'
import Item from './Item'
import deliveryNotestub from '../../stub/deliveryNote'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import { validationMixin } from 'vuelidate'
import Guid from 'guid'
const { required, between, maxLength, numeric } = require('vuelidate/lib/validators')

export default {
  components: {
    Item,
    MultiSelect,
    draggable
  },
  mixins: [validationMixin],
  data () {
    return {
      subtotal:null,
      newDeliveryNote: {
        delivery_note_date: null,
        delivery_note_number: null,
        customer_id: null,
        delivery_note_template_id: 1,
        notes: null,
        reference_number: null,
        items: [{
          ...deliveryNotestub,
          id: Guid.raw(),
        }],
      },
      customers: [],
      itemList: [],
      deliveryNoteTemplates: [],
      selectedCurrency: '',
      initLoading: false,
      isLoading: false,
      deliveryNotePrefix: null,
      deliveryNoteNumAttribute: null,
    }
  },
  validations () {
    return {
      newDeliveryNote: {
        delivery_note_date: {
          required
        },
        notes: {
          maxLength: maxLength(255)
        },
        reference_number: {
          maxLength: maxLength(255)
        }
      },
      selectedCustomer: {
        required
      },
      deliveryNoteNumAttribute: {
        required,
        numeric
      }
    }
  },
  destroyed () {
    if (this.selectAllDeliveryNoteItem) {
      this.reselectAllDeliveryNoteItems()
    }
    if(this.POHasItems){
      this.resetPOHasItem()
    }
    if(this.availableDeliveryNoteItems){
      this.resetAvailableDeliveryNoteItems()
    }
    if(this.deliveryNoteItemsStatus){
      this.resetDeliveryNoteItemsStatus()
    }
    if(this.POItems){
      this.resetPOItems()
    }
  },
  computed: {
    ...mapGetters('general', [
      'itemDiscount'
    ]),
    ...mapGetters('currency', [
      'defaultCurrency'
    ]),
    ...mapGetters('deliveryNote', [
      'getTemplateId',
      'selectedCustomer',
      'selectAllFieldItem'  ,
      'selectedDeliveryNoteItems'   ,
      'deliveryNoteItems' ,
      'POItems',
      'deliveryNoteItems',
      'deliveryNoteItemsStatus',
      'POHasItems'
    ]),
    currency () {
      return this.selectedCurrency
    },
    selectAllDeliveryNoteItem: {
      get: function () {
        return this.selectAllFieldItem
      },
      set: function (val) {
        this.setSelectAllStateItem(val)
      }
    },
    subtotalWithDiscount () {
      return this.subtotal
    },
    ItemId(){
      return Guid.raw()
    },
    // subtotal:{
    //   get: function () {
    //     console.log('get ')
    //     return this.deliveryNoteItems.reduce((a, b) => a + (b['total'] || 0), 0);
    //   },
    //   set: function (val) {
    //     console.log(val)
    //     this.newDeliveryNote.total = val
    //   }
    // },
    discount: {
      get: function () {
        return this.newDeliveryNote.discount
      },
      set: function (newValue) {
        if (this.newDeliveryNote.discount_type === 'percentage') {
          this.newDeliveryNote.discount_val = (this.subtotal * newValue) / 100
        } else {
          this.newDeliveryNote.discount_val = newValue * 100
        }

        this.newDeliveryNote.discount = newValue
      }
    },
  },
  watch: {
    selectedCustometer (newVal) {
      if (newVal && newVal.currency) {
        this.selectedCurrency = newVal.currency
      }
    },
    // subtotal (newValue) {
    //   return this.deliveryNoteItems.reduce((a, b) => a + (b['total'] || 0), 0);
    // }
  },
  created () {
    this.loadData()
    this.resetSelectedCustomer ()
  },
  methods: {
    ...mapActions('modal', [
      'openModal'
    ]),
    ...mapActions('deliveryNote', [
      'addDeliveryNote',
      'fetchCreateDeliveryNote',
      'fetchDeliveryNote',
      'resetSelectedCustomer',
      'selectCustomer',
      'updateDeliveryNote',
      'setSelectAllStateItem',
      'selectAllDeliveryNoteItems',
      'deleteMultipleItems',
      'addDeliveryNoteItem',
      'updateDeliveryNoteItem',
      'removeDeliveryNoteItem',
      'resetPOHasItem',
      'resetAvailableDeliveryNoteItems',
      'resetDeliveryNoteItemsStatus',
      'resetPOItems',
      'reselectAllDeliveryNoteItems'
    ]),
    ...mapActions('item', [
      'fetchItems',
      'fetchCompanyItems',
      'emptyItems'
    ]),
    async fetchInitialItems () {
      await this.fetchCompanyItems({
        orderByField: '',
        orderBy: '',
      })
    },
    async loadData () {
      if (this.$route.name === 'delivery-notes.edit') {
        this.initLoading = true
        let response = await this.fetchDeliveryNote(this.$route.params.id)

        if (response.data) {
          this.selectCustomer(response.data.deliveryNote.customer_id)
          this.newDeliveryNote = response.data.deliveryNote
          this.newDeliveryNote.delivery_note_date = moment(response.data.deliveryNote.delivery_note_date, 'YYYY-MM-DD').toString()
          this.deliveryNoteTemplates = response.data.deliveryNoteTemplates
          this.deliveryNotePrefix = response.data.delivery_note_prefix
          this.deliveryNoteNumAttribute = response.data.nextDeliveryNoteNumber
          this.subtotal = response.data.deliveryNote.items.reduce((a, b) => a + (b['total'] || 0), 0);
        }
        this.initLoading = false
        return
      }

      this.initLoading = true
      let response = await this.fetchCreateDeliveryNote()
      if (response.data) {
        this.deliveryNoteTemplates = response.data.deliveryNoteTemplates
        let today = new Date()
        this.newDeliveryNote.delivery_note_date = moment(today).toString()
        this.newDeliveryNote.expiry_date = moment(today).add(7, 'days').toString()
        this.itemList = response.data.items
        this.deliveryNotePrefix = response.data.delivery_note_prefix
        this.deliveryNoteNumAttribute = response.data.nextDeliveryNoteNumber
      }
      this.initLoading = false
    },

    removeCustomer () {
      this.resetSelectedCustomer()
    },
    editCustomer () {
      this.openModal({
        'title': this.$t('customers.edit_customer'),
        'componentName': 'CustomerModal',
        'id': this.selectedCustomer.id,
        'data': this.selectedCustomer
      })
    },
    openTemplateModal () {
      this.openModal({
        'title': this.$t('general.choose_template'),
        'componentName': 'PurchaseOrderTemplate',
        'data': this.deliveryNoteTemplates
      })
    },
    addItem () {
      if(this.POHasItems== true){
        if(this.deliveryNoteItemsStatus == true && this.deliveryNoteItems){
          console.log('this.deliveryNoteItemsStatus == true')
          this.addDeliveryNoteItem({...deliveryNotestub, id: Guid.raw()})
          this.subtotal = this.deliveryNoteItems.reduce((a, b) => a + (b['total'] || 0), 0);
        }
        else if(this.deliveryNoteItemsStatus == false){
          console.log('this.deliveryNoteItemsStatus == false')
          this.addDeliveryNoteItem({...deliveryNotestub, id: this.ItemId})
          this.subtotal = this.deliveryNoteItems.reduce((a, b) => a + (b['total'] || 0), 0);
        }

      }else{
        console.log('llll')
        this.newDeliveryNote.items.push({...deliveryNotestub, id: Guid.raw()})
        this.subtotal = this.newDeliveryNote.items.reduce((a, b) => a + (b['total'] || 0), 0);
      }
    },
    removeMultipleItems(){
      if(this.POHasItems){
        this.deleteMultipleItems()
        this.subtotal = this.deliveryNoteItems.reduce((a, b) => a + (b['total'] || 0), 0);
      }else{

      }
    },
    removeItem (itemId) {
      if(this.POHasItems == true &&  this.deliveryNoteItemsStatus == true){
        this.removeDeliveryNoteItem(itemId)
        this.subtotal = this.deliveryNoteItems.reduce((a, b) => a + (b['total'] || 0), 0);
      }else{
        this.newDeliveryNote.items.splice(itemId, 1)
        this.subtotal = this.newDeliveryNote.items.reduce((a, b) => a + (b['total'] || 0), 0);
      }
    },
    emptyItemArray(){
      this.newDeliveryNote.items.splice(0,  this.newDeliveryNote.items.length)
    },
    updateItem (data) {
      console.log(data)
      if(this.POHasItems == true && this.deliveryNoteItems && this.deliveryNoteItemsStatus==true){
        this.updateDeliveryNoteItem(data)
        this.subtotal = this.deliveryNoteItems.reduce((a, b) => a + (b['total'] || 0), 0);
      }
      else if(this.POHasItems== false &&  this.deliveryNoteItems && this.deliveryNoteItemsStatus == true){
       this.updateDeliveryNoteItem(data)
        this.subtotal = this.deliveryNoteItems.reduce((a, b) => a + (b['total'] || 0), 0);
      }
      else{
        console.log('update item')
        Object.assign(this.newDeliveryNote.items[data.index], {...data})
        this.subtotal = this.newDeliveryNote.items.reduce((a, b) => a + (b['total'] || 0), 0);
      }
    },
    submitDeliveryNoteData () {
      if (!this.checkValid()) {
        return false
      }

      this.isLoading = true
      this.newDeliveryNote.delivery_note_number = this.deliveryNotePrefix + '-' + this.deliveryNoteNumAttribute

      let data = {
        ...this.newDeliveryNote,
        delivery_note_date: moment(this.newDeliveryNote.delivery_note_date).format('DD/MM/YYYY'),
        supplier_id: null,
        delivery_note_template_id: this.getTemplateId,
        subtotal:this.subtotal
      }

      if (this.selectedCustomer != null) {
        data.customer_id = this.selectedCustomer.id
      }

      if (this.$route.name === 'delivery-notes.edit') {
        this.submitUpdate(data)
        return
      }
      this.submitSave(data)
    },
    submitSave (data) {

      this.addDeliveryNote(data).then((res) => {
        if (res.data) {
          window.toastr['success'](this.$t('deliveryNotes.created_message'))
          this.$router.push('/supplier/delivery-notes')
        }

        this.isLoading = false
      }).catch((err) => {
        console.log(err)
        this.isLoading = false
        if (err.response.data.errors.delivery_note_number) {
          window.toastr['error'](err.response.data.errors.delivery_note_number)
          return true
        }
        window.toastr['error'](err.response.data.message)
      })
    },
    submitUpdate (data) {
      this.updateDeliveryNote(data).then((res) => {
        if (res.data.success==true) {
          window.toastr['success'](this.$t('deliveryNotes.updated_message'))
          this.$router.push('/supplier/delivery-notes')
        }
        if (res.data.error === 'items_quantity_greater_po_item') {
          window.toastr['error'](this.$t('deliveryNotes.items_quantity_greater_po_item'))
        }

        this.isLoading = false
      }).catch((err) => {
         console.log(err)
        this.isLoading = false
        if (err.response.data.errors.delivery_note_number) {
          window.toastr['error'](err.response.data.errors.delivery_note_number)
          return true
        }
        window.toastr['error'](err.response.data.message)
      })
    },
    checkItemsData (index, isValid) {
      this.newDeliveryNote.items[index].valid = isValid
    },
    checkValid () {
      this.$v.newDeliveryNote.$touch()
      this.$v.selectedCustomer.$touch()
      window.hub.$emit('checkItems')
      let isValid = true
      this.newDeliveryNote.items.forEach((item) => {
        if (!item.valid) {
          isValid = false
        }
      })
      if (!this.$v.selectedCustomer.$invalid && this.$v.newDeliveryNote.$invalid === false && isValid === true) {
        return true
      }
      return false
    }
  }
}
</script>
<style scoped>
.item-checkbox{
  padding-left: 20px;
}
</style>
